import type { User } from '@core/entities/User';
import type {
  AuthRepository,
  LoginCredentials,
  LoginOutcome,
  RegisterPayload,
  ResetPasswordPayload,
  TwoFactorChallengePayload,
} from '@core/repositories/AuthRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { ensureCsrfCookie } from '@infrastructure/http/ensureCsrfCookie';
import { toAppError } from '@infrastructure/http/errorMapper';
import { toUser, type UserEnvelope, type UserResponse } from '@infrastructure/mappers/toUser';

interface LoginResponse {
  data?: UserResponse;
  requires_two_factor?: boolean;
  challenge_token?: string;
}

export class AuthRepositoryImpl implements AuthRepository {
  async login(credentials: LoginCredentials): Promise<LoginOutcome> {
    try {
      await ensureCsrfCookie();
      const response = await httpClient.post<LoginResponse>('/login', {
        email: credentials.email,
        password: credentials.password,
        remember: credentials.remember ?? false,
      });

      if (response.data.requires_two_factor && response.data.challenge_token) {
        return { status: 'two_factor_required', challengeToken: response.data.challenge_token };
      }

      return { status: 'authenticated', user: toUser(response.data.data as UserResponse) };
    } catch (error) {
      throw toAppError(error);
    }
  }

  async challengeTwoFactor(payload: TwoFactorChallengePayload): Promise<User> {
    try {
      const response = await httpClient.post<UserEnvelope>('/two-factor-challenge', {
        challenge_token: payload.challengeToken,
        code: payload.code,
      });
      return toUser(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async register(payload: RegisterPayload): Promise<User> {
    try {
      await ensureCsrfCookie();
      const response = await httpClient.post<UserEnvelope>('/register', {
        name: payload.name,
        email: payload.email,
        password: payload.password,
        password_confirmation: payload.passwordConfirmation,
      });
      return toUser(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async logout(): Promise<void> {
    try {
      await httpClient.post('/logout');
    } catch (error) {
      throw toAppError(error);
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await httpClient.get<UserEnvelope>('/user');
      return toUser(response.data.data);
    } catch (error) {
      const appError = toAppError(error);
      if ('status' in appError && (appError as { status?: number }).status === 401) {
        return null;
      }
      throw appError;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await ensureCsrfCookie();
      await httpClient.post('/forgot-password', { email });
    } catch (error) {
      throw toAppError(error);
    }
  }

  async resetPassword(payload: ResetPasswordPayload): Promise<void> {
    try {
      await ensureCsrfCookie();
      await httpClient.post('/reset-password', {
        token: payload.token,
        email: payload.email,
        password: payload.password,
        password_confirmation: payload.passwordConfirmation,
      });
    } catch (error) {
      throw toAppError(error);
    }
  }

  async resendVerificationEmail(): Promise<void> {
    try {
      await httpClient.post('/email/verification-notification');
    } catch (error) {
      throw toAppError(error);
    }
  }
}
